// src/utils/excelHoursParser.js
// Parsiranje Excel podataka za Glovo i Wolt sati

import { HOURS_IMPORT_CONFIG } from './excelImportConfig';

/**
 * Normalizira ključeve: mala slova, bez razmaka/specijalnih znakova
 */
function normalizeKey(key) {
  return String(key ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function normalizeRow(row) {
  const normalized = {};
  Object.keys(row).forEach((key) => {
    const k = normalizeKey(key);
    let v = row[key];
    if (typeof v === 'string') v = v.trim();
    normalized[k] = v;
  });
  return normalized;
}

function pickFirstValue(row, keys = []) {
  for (const k of keys) {
    const normalizedKey = normalizeKey(k);
    if (row[normalizedKey] !== undefined && row[normalizedKey] !== null && row[normalizedKey] !== '') {
      return row[normalizedKey];
    }
  }
  return undefined;
}

function parseNumberValue(raw) {
  if (raw === undefined || raw === null || raw === '') return 0;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
  const s = String(raw).trim().replace(/\s+/g, '').replace(/[^0-9,.-]/g, '');
  if (!s) return 0;
  const hasComma = s.includes(',');
  const hasDot = s.includes('.');
  let normalized = s;

  // Ako imamo i točku i zarez: zadnji separator je decimalni, ostali su tisućice.
  if (hasComma && hasDot) {
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');
    const decimalSep = lastComma > lastDot ? ',' : '.';
    const thousandSep = decimalSep === ',' ? '.' : ',';
    normalized = s.split(thousandSep).join('');
    if (decimalSep === ',') normalized = normalized.replace(',', '.');
  } else if (hasComma) {
    // Samo zarez -> decimalni zarez.
    normalized = s.replace(',', '.');
  } else {
    // Samo točka ili cijeli broj.
    normalized = s;
  }

  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : 0;
}

function parseGlovoDistanceMeters(raw) {
  // Glovo distance stupac je u kilometrima -> pretvaramo u metre.
  let km = 0;
  if (typeof raw === 'string') {
    const compact = raw.trim().replace(/\s+/g, '');
    // Ako je oblik tipa 812.000 ili 812,000 tretiraj separator kao tisućice.
    const groupedThousandsPattern = /^[+-]?\d{1,3}(?:[.,]\d{3})+$/;
    if (groupedThousandsPattern.test(compact)) {
      const digitsOnly = compact.replace(/[^\d-]/g, '');
      km = parseInt(digitsOnly, 10) || 0;
    } else {
      km = parseNumberValue(compact);
    }
  } else {
    km = parseNumberValue(raw);
  }
  return Math.max(0, km * 1000);
}

function parseWoltDistanceMeters(raw) {
  // Wolt distance stupac je uvijek cijeli broj metara.
  // Točka/zarez tretiramo kao tisućice (nikad decimalni dio za distance).
  if (raw === undefined || raw === null || raw === '') return 0;
  if (typeof raw === 'number') {
    return Math.max(0, Math.trunc(raw));
  }
  const digitsOnly = String(raw).replace(/[^\d-]/g, '');
  if (!digitsOnly || digitsOnly === '-') return 0;
  const parsed = parseInt(digitsOnly, 10);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

/**
 * Glovo Excel: stupac "sum CDT in minutes" (radni sati u minutama), "Rider ID" = glovoId
 * Vraća decimalne sate (minute / 60) - spremamo s punom preciznošću za akumulaciju sekundi
 */
export function parseGlovoExcel(data, people) {
  const results = [];
  const cfg = HOURS_IMPORT_CONFIG.glovo;
  for (const row of data) {
    const n = normalizeRow(row);
    const riderIdRaw = pickFirstValue(n, cfg.idColumnKeys);
    const sumCdtRaw = pickFirstValue(n, cfg.amountColumnKeys);
    const distanceMetersRaw = pickFirstValue(n, cfg.distanceMetersColumnKeys);

    let glovoId = null;
    if (typeof riderIdRaw === 'string') {
      glovoId = riderIdRaw.trim().toLowerCase();
    } else if (riderIdRaw != null) {
      glovoId = String(riderIdRaw).trim().toLowerCase();
    }
    if (!glovoId) continue;

    const person = people.find(
      (p) => p.glovoId && p.glovoId.trim().toLowerCase() === glovoId
    );
    if (!person) continue;

    const minutes = parseNumberValue(sumCdtRaw);
    const decimalHours = minutes / 60;
    const glovoDistanceMeters = parseGlovoDistanceMeters(distanceMetersRaw);
    results.push({ userId: person.id, glovoHours: decimalHours, glovoDistanceMeters });
  }
  return results;
}

/**
 * Wolt Excel: stupac "Id" = woltId, "Total on-task time" format "5h 20m"
 * Parsira format Xh Ym u decimalne sate
 */
export function parseWoltExcel(data, people) {
  const results = [];
  const cfg = HOURS_IMPORT_CONFIG.wolt;
  const timeRegex = /(\d+)\s*h\s*(\d+)\s*m/i;
  const timeRegexHOnly = /(\d+)\s*h/i;
  const timeRegexMOnly = /(\d+)\s*m/i;

  for (const row of data) {
    const n = normalizeRow(row);
    const idRaw = pickFirstValue(n, cfg.idColumnKeys);
    const totalRaw = pickFirstValue(n, cfg.amountColumnKeys);
    const distanceMetersRaw = pickFirstValue(n, cfg.distanceMetersColumnKeys);

    let woltId = null;
    if (typeof idRaw === 'string') {
      woltId = idRaw.trim().toLowerCase();
    } else if (idRaw != null) {
      woltId = String(idRaw).trim().toLowerCase();
    }
    if (!woltId) continue;

    const person = people.find(
      (p) => p.woltId && p.woltId.trim().toLowerCase() === woltId
    );
    if (!person) continue;

    let decimalHours = 0;
    if (totalRaw != null) {
      const str = String(totalRaw).trim();
      const match = str.match(timeRegex);
      if (match) {
        const h = parseInt(match[1], 10) || 0;
        const m = parseInt(match[2], 10) || 0;
        decimalHours = h + m / 60;
      } else {
        const hMatch = str.match(timeRegexHOnly);
        const mMatch = str.match(timeRegexMOnly);
        const h = hMatch ? parseInt(hMatch[1], 10) : 0;
        const m = mMatch ? parseInt(mMatch[1], 10) : 0;
        decimalHours = h + m / 60;
      }
    }

    const woltDistanceMeters = parseWoltDistanceMeters(distanceMetersRaw);
    results.push({ userId: person.id, woltHours: decimalHours, woltDistanceMeters });
  }
  return results;
}
